import { type ReactNode, useContext } from 'react';

import { TimeseriesChartConfigContext } from '../context/TimeseriesChartConfig.context.js';
import {
  TimeseriesChartConfigProperties,
  type TimeseriesChartConfigProps,
  TimeseriesChartConfiguration,
  TimeseriesChartSlots,
} from '../types/timeseries-config.js';
import {
  createTimeseriesConfiguration,
  deserializeConfig,
} from '../utils/chart-configuration.js';
import { iterateConfigSlots } from '../utils/iterate-config-slots.js';

/**
 * Returns TimeseriesChartConfiguration from context, slots or default values
 * @param children - TimeseriesChart children
 * @param overrides - Chart config overrides
 * @returns TimeseriesChartConfiguration
 */
export const useTimeseriesChartConfig = (
  children?: ReactNode,
  overrides?: TimeseriesChartConfigProps,
): TimeseriesChartConfiguration => {
  let contextConfig = useContext(TimeseriesChartConfigContext);

  if (isSerializedConfig(contextConfig)) {
    contextConfig = deserializeConfig(contextConfig);
  }

  const slots: TimeseriesChartSlots = iterateConfigSlots(children);

  return createTimeseriesConfiguration(slots, contextConfig, overrides || {});
};

/**
 * Checks if the contextConfig is a serialized-string configuration
 * @param contextConfig - context configuration.
 * @returns true if the received contextConfig is a string.
 */
function isSerializedConfig(
  contextConfig: TimeseriesChartConfigProperties | string,
): contextConfig is string {
  return typeof contextConfig === 'string';
}
